import { FunctionComponent, useCallback, useState } from 'react'

import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import { desktopCSS, mobileAndTabletCSS } from '@measures/responsive'
import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage'
import translate from '@i18n'
import useABTestValue from '@hooks/useABTestValue'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import useIsImmersiveHeaderActive from '@hooks/useIsImmersiveHeaderActive'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import Blink from '@components/Blink'
import SvgIcon from '@components/SvgIcon'

const {
  search: { sectionUrl },
  aiSearch: { localStorageHideAISearchBadgeKey },
} = config

const StyledLink = styled(Blink)<{ isBadgeVisible: boolean }>`
  ${({
    theme: {
      colors: { iconBrand },
    },
    isBadgeVisible,
  }) => css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    appearance: none;
    text-decoration: none;
    user-select: none;

    width: 48px;
    height: 48px;

    user-select: none;

    ${isBadgeVisible &&
    css`
      margin-right: 12px;
    `}

    ${desktopCSS(css`
      width: 40px;
      height: 40px;
    `)}

    &:hover {
      svg {
        fill: ${iconBrand};
      }
    }
  `}
`

const StyledSvgIcon = styled(SvgIcon)<{ isImmersiveHeaderActive: boolean }>`
  ${({
    theme: {
      colors: { staticWhite },
    },
    isImmersiveHeaderActive,
  }) => css`
    ${isImmersiveHeaderActive &&
    css`
      ${mobileAndTabletCSS(css`
        fill: ${staticWhite};
      `)}
    `}
  `}
`

const StyledBadge = styled.div`
  ${({
    theme: {
      colors: { iconBrand, staticWhite },
      typography: {
        subheads: {
          xsmall2: { bundledCSS: xsmall2SubheadCSS },
        },
      },
    },
  }) => css`
    ${xsmall2SubheadCSS};
    line-height: 1;
    text-transform: uppercase;

    background-color: ${iconBrand};
    color: ${staticWhite};
    border-radius: 10px;

    position: absolute;
    z-index: 1;
    padding: 3px 4px;
    top: 7px;
    left: 26px;

    ${desktopCSS(css`
      top: 3px;
      left: 22px;
    `)}
  `}
`

const EnhancedSearchLink: FunctionComponent = () => {
  const theme = useTheme()
  const isImmersiveHeaderActive = useIsImmersiveHeaderActive()
  const { isSearchPage } = useGetPageIdentity()
  const [shouldShowBadge, setShouldShowBadge] = useState<boolean>(
    !getLocalStorageItem(localStorageHideAISearchBadgeKey),
  )
  const isAISearchBadgeEnabled = useABTestValue('ai-search-badge') === 'show'
  const isBadgeVisible = isAISearchBadgeEnabled && shouldShowBadge

  const trackingOnClick = useCallback<TrackingFnType>(
    () => ({
      event: 'et_open',
      chatbot_entrypoint: 'search_icon_header',
      element_status: isBadgeVisible ? 'badge' : 'no badge',
    }),
    [isBadgeVisible],
  )

  const handleTrack = useTracking(trackingOnClick)

  const hideBadge = useCallback(() => {
    setShouldShowBadge((shouldShowBadgeValue) => {
      if (shouldShowBadgeValue) {
        setLocalStorageItem(localStorageHideAISearchBadgeKey, 'true')
        return false
      }
      return shouldShowBadgeValue
    })
  }, [])

  const handleClick = useCallback(() => {
    handleTrack()
    hideBadge()
  }, [handleTrack, hideBadge])

  return (
    <StyledLink
      href={sectionUrl}
      onClick={handleClick}
      ariaLabel="Search"
      isBadgeVisible={isBadgeVisible}
    >
      {isSearchPage ? (
        <SvgIcon
          iconName="ai-search-solid"
          size={24}
          color={theme.colors.iconBrand}
        />
      ) : (
        <StyledSvgIcon
          iconName="ai-search"
          size={24}
          color={theme.colors.iconDefault}
          isImmersiveHeaderActive={isImmersiveHeaderActive}
        />
      )}
      {isBadgeVisible && (
        <StyledBadge>{translate('aiSearch.badge')}</StyledBadge>
      )}
    </StyledLink>
  )
}

export default EnhancedSearchLink
